/**
 * Employment end date helpers.
 *
 * Expected API (feature-safe fallbacks if missing):
 * - `employment.employment_ends` (YYYY-MM-DD) on employee / nested user.employment
 * - aliases: `employment.end_date`, `employment_ends`, `end_date`
 *
 * Hours: days AFTER the end date are blocked (last day is still allowed).
 * New plans: days ON or AFTER the end date are blocked.
 */

export function normalizeDateKey(value) {
  if (!value) return null;
  const key = String(value).substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
}

export function getEmploymentEndDate(record) {
  if (!record) return null;
  return (
    normalizeDateKey(record.employment_ends) ||
    normalizeDateKey(record.employment?.employment_ends) ||
    normalizeDateKey(record.employment?.end_date) ||
    normalizeDateKey(record.end_date) ||
    normalizeDateKey(record.user?.employment_ends) ||
    normalizeDateKey(record.user?.employment?.employment_ends) ||
    normalizeDateKey(record.user?.employment?.end_date)
  );
}

export function isEmploymentEnded(record, asOf = null) {
  const end = getEmploymentEndDate(record);
  if (!end) return false;
  const day = normalizeDateKey(asOf) || normalizeDateKey(new Date().toISOString());
  return Boolean(day && day >= end);
}

/** Hours / PMDatePicker: cannot pick a day after the last employment day. */
export function isDateAfterEmploymentEnd(date, record) {
  const end = getEmploymentEndDate(record);
  const day = normalizeDateKey(date);
  if (!end || !day) return false;
  return day > end;
}

/** New project plans: cannot schedule on or after the end date. */
export function isDateOnOrAfterEmploymentEnd(date, record) {
  const end = getEmploymentEndDate(record);
  const day = normalizeDateKey(date);
  if (!end || !day) return false;
  return day >= end;
}

export function expandAbsenceDateKeys(absences = []) {
  const keys = new Set();
  (absences || []).forEach((absence) => {
    const from = normalizeDateKey(absence.date_from || absence.from || absence.start_date);
    const to = normalizeDateKey(absence.date_too || absence.date_to || absence.end_date) || from;
    if (!from) return;
    const start = new Date(`${from}T12:00:00`);
    const end = new Date(`${to}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      keys.add(normalizeDateKey(d));
    }
  });
  return keys;
}
